import { type AuthProvider, PreviousLocationStorageKey } from 'react-admin';
import LogtoClient from '@logto/browser';


export const LogtoAuthProvider = (
    client: LogtoClient,
    {
        loginRedirectUri,
        logoutRedirectUri,
        redirectOnCheckAuth = true,
    }: {
        loginRedirectUri?: string;
        logoutRedirectUri?: string;
        redirectOnCheckAuth?: boolean;
    } = {
        redirectOnCheckAuth: true,
    }
): AuthProvider => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';
    const redirectUri = loginRedirectUri ??
        `${window.location.origin}/auth-callback`;

    // Hands control over to Logto. window.location.assign() doesn't stop JS, so
    // without a guard every parallel checkAuth/checkError starts its own sign-in
    // (each overwriting the stored PKCE state), and react-admin — seeing them
    // resolve — keeps rendering, hits the API, gets 401 and calls logout(). The
    // signOut navigation then wins over the sign-in one, lands on /login and the
    // whole thing starts over. Hence: one redirect per page load, and a promise
    // that never settles so react-admin takes no further action.
    const redirectToSignIn = async (previousLocation: string): Promise<never> => {
        if (!redirecting) {
            redirecting = true;
            localStorage.setItem(PreviousLocationStorageKey, previousLocation);
            await client.signIn({ redirectUri });
        }
        return new Promise<never>(() => {});
    };

    return {
        // Used when the redirection to Logto is done from a custom login page
        async login() {
            return redirectToSignIn('/');
        },
        // called when the user clicks on the logout button
        async logout() {
            await client.signOut(logoutRedirectUri || window.location.origin);
            return false;
        },
        // called when the API returns an error
        async checkError({ status }) {
            if (status !== 401 && status !== 403) {
                return;
            }
            // isAuthenticated() only checks for a stored id token — it stays true even
            // after the refresh token is gone. Probe getAccessToken to confirm we can
            // still obtain a valid access token; if not, force re-authentication.
            // If we can, the API rejected a valid token (missing scope, audience
            // mismatch): signing in again would change nothing, so stay put.
            try {
                if (await client.getAccessToken(apiUrl)) {
                    return;
                }
            } catch {
                // fall through to re-auth
            }
            if (redirectOnCheckAuth) {
                return redirectToSignIn(window.location.href);
            }
            throw new Error('Unauthorized');
        },
        // called when the user navigates to a new location, to check for authentication
        async checkAuth() {
            if (await client.isAuthenticated()) {
                return;
            }
            // The login page checks auth on mount too; let it render its button
            // instead of bouncing straight back to Logto (e.g. right after logout).
            if (redirectOnCheckAuth && window.location.pathname !== '/login') {
                return redirectToSignIn(window.location.href);
            }
            throw new Error('Unauthorized');
        },
        // called when the user navigates to a new location, to check for permissions / roles
        async getPermissions() {
            if (!(await client.isAuthenticated())) {
                return;
            }

            const claims = await client.getAccessTokenClaims(apiUrl);
            return claims.scope;
        },
        async getIdentity() {
            const user = await client.fetchUserInfo();
            return {
                id: user.identities?.[0].userId || '',
                fullName: user.name ?? '',
                avatar: user.picture ?? undefined,
            };
        },
        async handleCallback() {
            if (!handleCallbackPromise) {
                handleCallbackPromise = handleSignInCallback(client, apiUrl);
            }
            return handleCallbackPromise;
        },
    };
};

// Rejections end up on react-admin's AuthError page, which stops the flow —
// every failure here must reject rather than trigger another sign-in.
const handleSignInCallback = async (client: LogtoClient, apiUrl: string) => {
    const params = new URLSearchParams(window.location.search);
    // Logto reports misconfiguration (unknown API resource, unregistered
    // redirect URI, ...) by redirecting back with error= instead of code=.
    const error = params.get('error');
    if (error) {
        throw {
            redirectTo: false,
            message: `Logto: ${params.get('error_description') || error}`,
        };
    }
    if (!params.has('code') || !params.has('state')) {
        throw { message: 'Failed to handle login callback.' };
    }
    try {
        await client.handleSignInCallback(window.location.href);
    } catch (e: any) {
        throw { redirectTo: false, message: e.message };
    }
    // Signed in to Logto but no token for the API (resource not assigned to the
    // app, refresh token not issued): redirecting to sign-in again would loop,
    // because Logto still has a session and bounces straight back here.
    try {
        await client.getAccessToken(apiUrl);
    } catch (e: any) {
        await client.clearAllTokens();
        throw {
            redirectTo: false,
            message: `Cannot get access token for ${apiUrl}: ${e.message}`,
        };
    }
};

let redirecting = false;
let handleCallbackPromise: Promise<void> | null = null;
